import React, { useState, useRef, useEffect } from 'react';
import { venueDetailsMapping } from '../misc/venueDetailsMapping';
import DangerIcon from '../assets/modal-icon-danger.png';
import '../styles/TagsMatching.css';

const TagsMatching = ({
  user_id,
  accountStatus,
  selectedVenues,
  setSelectedVenues,
  setTagFilteredVenues,
}) => {
  const options = [
    { value: 'Garden', label: 'Garden' },
    { value: 'Mountain', label: 'Mountain View' },
    { value: 'Ocean', label: 'Ocean View' },
    { value: 'Desert', label: 'Desert View' },
    { value: 'Winery', label: 'Winery' },
    { value: 'National Park', label: 'National Park' },
    { value: 'Ranch', label: 'Ranch' },
    { value: 'Golf Club', label: 'Golf Club' },
    { value: 'Luxury Hotel', label: 'Luxury Hotel' },
    { value: 'Landmark Hotel', label: 'Landmark Hotel' },
    { value: 'City Hall', label: 'City Hall' },
  ];

  const [selectedTags, setSelectedTags] = useState([]);
  const [venuesData, setVenuesData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const dropdownRef = useRef(null);
  const venuesRef = useRef(null);

  // Close dropdown on click *outside* both the dropdown and the venues list
  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickInsideDropdown = dropdownRef.current?.contains(e.target);
      const clickInsideVenues = venuesRef.current?.contains(e.target);
      if (!clickInsideDropdown && !clickInsideVenues) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, []);

  // Fetch venues whenever selectedTags changes
  useEffect(() => {
    const fetchVenues = async () => {
      if (selectedTags.length === 0) {
        setVenuesData([]);
        setTagFilteredVenues([]);
        return;
      }
      setIsLoading(true);
      setApiError(null);

      const qs = selectedTags.map((t) => `tags=${encodeURIComponent(t)}`).join('&');
      try {
        const res = await fetch(
          `https://api.bestday2marry.com/search_venues_by_tags?${qs}`
        );
        const data = await res.json();
        if (data.status === 'success') {
          setVenuesData(data.venues);
          setTagFilteredVenues(data.venues);
        } else {
          setApiError(data.message || 'Error fetching venues.');
        }
      } catch (err) {
        console.error(err);
        setApiError('Error fetching venues. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchVenues();
  }, [selectedTags, setTagFilteredVenues]);

  const handleTagToggle = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const getMaxAllowed = () => {
    const plan = accountStatus?.subscription.plan;
    return plan === 'trial' || plan === 'essentials' ? 1 : Infinity;
  };

  const handleVenueSelection = (name) => {
    if (!accountStatus) {
      alert('Loading account status...');
      return;
    }
    const max = getMaxAllowed();
    if (!selectedVenues.includes(name)) {
      if (selectedVenues.length >= max) {
        setModalMessage(
          'Due to your current plan, you can only select one venue. Upgrade to view more.'
        );
        setShowModal(true);
        return;
      }
      setSelectedVenues([...selectedVenues, name]);
    } else {
      setSelectedVenues(selectedVenues.filter((n) => n !== name));
    }
  };

  return (
    <div className="tags-matching-page-container">
      <div className="tags-matching-container">
        <div className="tags-matching-header">
          <h1>Choose Your Dream Venue for Wedding</h1>
          <p>Select the venue types you’d love most.</p>
        </div>

        <div
          className={`custom-tags-selector ${isDropdownOpen ? 'open' : ''}`}
          ref={dropdownRef}
        >
          <div
            className="selected-tags-field"
            onClick={() => setIsDropdownOpen(o => !o)}
          >
            {selectedTags.length ? (
              <div className="pill-container">
                {selectedTags.map(tag => (
                  <span
                    key={tag}
                    className="pill selected"
                    onClick={e => {
                      e.stopPropagation();
                      handleTagToggle(tag);
                    }}
                  >
                    {options.find(o => o.value === tag)?.label || tag}
                  </span>
                ))}
              </div>
            ) : (
              <span className="placeholder">Select venue types...</span>
            )}
            <span className="dropdown-arrow">&#9662;</span>
          </div>

          {isDropdownOpen && (
            <div className="options-container">
              <div className="pill-container">
                {options.map(opt => (
                  <span
                    key={opt.value}
                    className={`pill ${
                      selectedTags.includes(opt.value) ? 'selected' : ''
                    }`}
                    onClick={() => handleTagToggle(opt.value)}
                  >
                    {opt.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {isLoading && <p>Loading venues...</p>}
      {apiError && <p className="error">{apiError}</p>}

      {venuesData.length > 0 && (
        <div className="venues-bucket" ref={venuesRef}>
          {venuesData.map((venue, i) => {
            const det = venueDetailsMapping[venue.name];
            if (!det) return null;
            return (
              <div
                key={i}
                className={`venue-card ${
                  selectedVenues.includes(venue.name) ? 'selected' : ''
                }`}
                onClick={() => handleVenueSelection(venue.name)}
              >
                <img
                  src={det.imageUrl}
                  alt={det.title}
                  className="venue-image"
                />
                <div className="card-content">
                  <h3 className="venue-title">{det.title}</h3>
                  <p className="venue-caption">{det.caption}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <img src={DangerIcon} alt="Danger" className="danger-icon" />
            <p>{modalMessage}</p>
            <button
              className="modal-button"
              onClick={() => setShowModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagsMatching;